import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarIcon, TagIcon, UserGroupIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import apiService from '../services/api';

interface NewsItem {
  _id: string;
  title: string;
  category: string;
  summary: string;
  content: string;
  author: string;
  tags: string[];
  isPublished: boolean;
  publishDate?: string;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
  coverImage?: string;
}

const Recruitment: React.FC = () => {
  const navigate = useNavigate();
  const [recruitmentNews, setRecruitmentNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRecruitmentNews();
  }, []);

  const fetchRecruitmentNews = async () => {
    setLoading(true);
    try {
      const response = await apiService.get<any>('/news', { category: '招生/招聘信息' });
      setRecruitmentNews(response.data || []);
    } catch (error) {
      console.error('获取招生/招聘信息失败:', error);
      setRecruitmentNews([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto py-8">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8">
      {/* 页面标题 */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">招生/招聘信息</h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          欢迎有志于从事钙钛矿材料与器件研究的优秀人才加入我们的团队！
          我们提供硕士、博士研究生招生以及博士后、科研助理等职位招聘信息。
        </p>
      </div>

      {/* 招生/招聘信息列表 */}
      {recruitmentNews.length > 0 ? (
        <div className="space-y-8">
          {recruitmentNews.map((item) => (
            <div key={item._id} className="card hover:shadow-lg transition-shadow duration-300">
              <div className="flex flex-col lg:flex-row gap-6">
                {/* 封面图片 */}
                {item.coverImage && (
                  <div className="lg:w-1/3">
                    <img 
                      src={item.coverImage} 
                      alt={item.title}
                      className="w-full h-48 lg:h-full object-cover rounded-lg"
                    />
                  </div>
                )}
                
                {/* 内容 */}
                <div className={`${item.coverImage ? 'lg:w-2/3' : 'w-full'}`}>
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                    <span className="flex items-center">
                      <CalendarIcon className="w-4 h-4 mr-1" />
                      {formatDate(item.publishDate || item.createdAt)}
                    </span>
                    <span className="flex items-center">
                      <UserGroupIcon className="w-4 h-4 mr-1" />
                      {item.author}
                    </span>
                    <span className="flex items-center">
                      <TagIcon className="w-4 h-4 mr-1" />
                      {item.viewCount} 次浏览
                    </span>
                  </div>
                  
                  <h2 className="text-2xl font-bold text-gray-900 mb-3 hover:text-primary-600 cursor-pointer"
                      onClick={() => navigate(`/news/${item._id}`)}>
                    {item.title}
                  </h2>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">{item.summary || item.content}</p>
                  
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {item.tags.map((tag, index) => (
                        <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <button
                    onClick={() => navigate(`/news/${item._id}`)}
                    className="btn btn-primary btn-sm"
                  >
                    查看详情
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <AcademicCapIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">暂无招生/招聘信息</h3>
          <p className="text-gray-600 mb-6">我们正在准备最新的招生和招聘信息，请稍后再来查看。</p>
          <button
            onClick={() => navigate('/contact')}
            className="btn btn-primary"
          >
            联系我们
          </button>
        </div>
      )}

      {/* 联系信息 */}
      <div className="mt-16 bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-8">
        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">欢迎加入我们的团队</h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            如果您对我们的研究方向感兴趣，或者希望了解更多关于招生和招聘的信息，
            欢迎随时与我们联系。我们期待与您一起探索钙钛矿材料与器件的前沿科学！
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="btn btn-primary"
            >
              联系我们
            </button>
            <button
              onClick={() => navigate('/research')}
              className="btn btn-outline"
            >
              了解研究方向
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Recruitment;




